import type { FC } from 'react'

import { StyledText } from '@skytech/ui'
import { Badge } from '@skytech/ui/Badge'
import { Button } from '@skytech/ui/Button'
import CheckIcon from 'mdi-react/CheckIcon'
import CloseIcon from 'mdi-react/CloseIcon'
import { twJoin } from 'tailwind-merge'

import type { SubscriptionService as SubscriptionServiceType } from '@/api/subscriptionServices'
import { useAppContext } from '@/App.context'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import { useIsThreeStepIT } from '@/hooks/user'

import { Translation } from '../i18n/Translation'

import { getBadgeTypeFromStatus } from './helpers'
import type { OrderStatus } from './types'

import './SubscriptionService.styles.scss'

const disabledStatusIds = [0, 1, 3]

const statusMap: Record<number, OrderStatus> = {
  0: 'PROCESSING',
  1: 'PROCESSING',
  2: 'ACTIVE',
  3: 'UNDER CANCELLATION',
}

export const SubscriptionService: FC<{
  service: SubscriptionServiceType
  onSelectSub: (selectedSub: SubscriptionServiceType) => void
}> = ({ service, onSelectSub }) => {
  const { whiteLabel } = useAppContext()
  const isThreeStepIT = useIsThreeStepIT()

  const isAddSubButton = !service.status || service.status === 1
  const isDisabled = Boolean(
    typeof service.status === 'number' && disabledStatusIds.includes(service.status)
  )

  const status = service.status !== null ? statusMap[service.status] : null

  const badgeType = status && getBadgeTypeFromStatus(status)

  return (
    <div key={service.id} className={'sub-service ' + whiteLabel}>
      <div className="service-text">
        <h3>{service.name}</h3>
        {badgeType && (
          <div className="order-3 mt-1 md:order-none md:mt-0">
            <Badge type={badgeType} className="ml-2.5 mt-1.5 text-sm md:order-none">
              {' '}
              <Translation id={status} />{' '}
            </Badge>
          </div>
        )}

        <div className="price">
          <Translation
            id="Includes {price} in salary deduction per month"
            service={service.name}
            price={
              <StyledText>
                <FormattedNumberWithCurrency
                  isTextBold
                  amount={service.price}
                  currency={service.currency}
                  className="currency"
                />
              </StyledText>
            }
          />
        </div>
      </div>

      {!isDisabled && (
        <div className="ui-button-is-responsive">
          <Button
            className={`sub-service-button w-full ${isAddSubButton ? 'add' : 'remove'}`}
            onClick={() => onSelectSub(service)}
            Icon={
              isAddSubButton ? (
                <CheckIcon
                  className={twJoin(isThreeStepIT ? 'fill-threeStepIT-success' : 'fill-green-500')}
                />
              ) : (
                <CloseIcon
                  className={twJoin(isThreeStepIT ? 'fill-threeStepIT-failure' : 'fill-red-500')}
                />
              )
            }
          >
            <Translation id={isAddSubButton ? 'Add' : 'Cancel'} />
          </Button>
        </div>
      )}
    </div>
  )
}
